import { useEffect } from 'react';

import { Book, Course, DeliverableDiffPayload, LicenseApi, LicenseDeliverable, LicenseDetail, LicenseFormPayload, LicenseUpdatePayload, Product } from 'apis/LicenseApi';
import { DEFAULT_DELIVERABLE, DEFAULT_FORM, DELIVERABLE_TYPE } from 'pages/licenses/combo_license/constants';
import { ComboLicenseForm, type FormErrors } from 'pages/licenses/combo_license/components';
import { validateLicenseForm } from 'pages/licenses/combo_license/validateLicenseForm';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { helpContent } from 'pages/licenses/combo_license/help';
import { PageHeader } from 'components/layout/page';
import { Alert } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

const getDeliverable = (deliverables: LicenseDeliverable[], type: number): LicenseFormPayload['practiceTests'] => {

    const d = deliverables.find(item => item.DeliverableType === type);

    if (!d) return { ...DEFAULT_DELIVERABLE };

    return {
        contentIds: d.ContentIds ? d.ContentIds.split(',').filter(Boolean).map(Number) : [],
        maxSeatsPerUser: d.MaxSeatsPerUser,
        maxUses: d.MaxUses,
        seats: d.Seats,
        usesPerCode: d.UsesPerCode
    };

};

const mapDetailToForm = (detail: LicenseDetail): LicenseFormPayload => ({
    id: detail.Id,
    name: detail.Name,
    allowAssociate: detail.AllowAssociate,
    allowOffline: detail.AllowOffline,
    books: getDeliverable(detail.Deliverables, DELIVERABLE_TYPE.EBOOK),
    courses: getDeliverable(detail.Deliverables, DELIVERABLE_TYPE.COURSE),
    licenseDuration: detail.LicenseDuration,
    licenseType: detail.LicenseType,
    practiceTests: getDeliverable(detail.Deliverables, DELIVERABLE_TYPE.PRACTICE_TEST),
    prefix: detail.Prefix ?? '',
    usageDays: detail.UsageDays
});

export type ComboLicenseProps = {
    licenseId: number;
    onBack: () => void;
};

export const ComboLicense = (props: ComboLicenseProps) => {

    const { formatMessage } = useIntl();

    const { licenseId } = props;

    const [state, setState] = useSuperState({
        apiError: null as string,
        apiSuccess: null as string,
        books: [] as Book[],
        courses: [] as Course[],
        errors: {} as FormErrors,
        form: { ...DEFAULT_FORM } as LicenseFormPayload,
        loading: true,
        original: { ...DEFAULT_FORM } as LicenseFormPayload,
        products: [] as Product[],
        saving: false
    });

    const clearError = () => setState({ apiError: null });

    const clearSuccess = () => setState({ apiSuccess: null });

    const diffDeliverable = (key: 'books' | 'courses' | 'practiceTests'): DeliverableDiffPayload => {

        const original = state.original[key];
        const current = state.form[key];
        const originalSet = new Set(original.contentIds);
        const currentSet = new Set(current.contentIds);

        return {
            addedIds: current.contentIds.filter(id => !originalSet.has(id)),
            maxSeatsPerUser: current.maxSeatsPerUser,
            maxUses: current.maxUses,
            removedIds: original.contentIds.filter(id => !currentSet.has(id)),
            seats: current.seats,
            usesPerCode: current.usesPerCode
        };

    };

    const buildUpdatePayload = (): LicenseUpdatePayload => ({
        id: licenseId,
        name: state.form.name,
        allowAssociate: state.form.allowAssociate,
        allowOffline: state.form.allowOffline,
        books: diffDeliverable('books'),
        courses: diffDeliverable('courses'),
        licenseDuration: state.form.licenseDuration,
        licenseType: state.form.licenseType,
        practiceTests: diffDeliverable('practiceTests'),
        prefix: state.form.prefix,
        usageDays: state.form.usageDays
    });

    const loadData = async () => {

        setState({ loading: true });

        try {

            const [detail, productsRes, coursesRes, booksRes] = await Promise.all([
                LicenseApi.GetById(licenseId),
                LicenseApi.GetProduct().catch(() => [] as Product[]),
                LicenseApi.GetCourse().catch(() => [] as Course[]),
                LicenseApi.GetBook().catch(() => [] as Book[])
            ]);

            const products = productsRes ?? [];
            const courses = coursesRes ?? [];
            const books = booksRes ?? [];

            setState({ books, courses, form: mapDetailToForm(detail), loading: false, original: mapDetailToForm(detail), products });

        } catch {

            setState({ loading: false });

        }

    };

    const handleSave = async () => {

        const errors = validateLicenseForm(state.form);

        if (Object.keys(errors).length > 0) {

            setState({ errors });
            return;

        }

        setState({ apiError: '', apiSuccess: '', saving: true });

        try {

            const result = await LicenseApi.Update(buildUpdatePayload());

            if (result?.Done === false) {

                setState({ apiError: result.Message ?? 'An error occurred.', saving: false });
                return;

            }

            setState({ apiSuccess: 'License saved successfully.', original: state.form, saving: false });

        } catch {

            setState({ apiError: 'An unexpected error occurred.', saving: false });

        }

    };

    useEffect(() => {

        loadData();

    }, [licenseId]);

    return (
        <div className="combo-license">
            <PageHeader
                cta={{
                    disabled: state.saving,
                    label: state.saving
                        ? formatMessage({ id: 'saving.bdfa2829', defaultMessage: 'Saving…' })
                        : formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' }),
                    onClick: handleSave
                }}
                current={state.form.name || formatMessage({ id: 'edit_license.ae9a1b44', defaultMessage: 'Edit License' })}
                helpContent={helpContent}
                title={state.form.name || formatMessage({ id: 'edit_license.ae9a1b44', defaultMessage: 'Edit License' })}
                trail={[{ label: formatMessage({ id: 'licenses.11b1c36a', defaultMessage: 'Licenses' }), to: '/licenses/search' }]}
            />

            {state.apiError && (
                <Alert dismissible onClose={clearError} show={!!state.apiError} variant={'danger'}>
                    {state.apiError}
                </Alert>
            )}

            {state.apiSuccess && (
                <Alert dismissible onClose={clearSuccess} show={!!state.apiSuccess} variant={'success'}>
                    {state.apiSuccess}
                </Alert>
            )}

            {state.loading ? (
                <div className={'d-flex align-items-center justify-content-center p-5'}>
                    <GMetrixLoader />
                </div>
            ) : (
                <ComboLicenseForm
                    books={state.books}
                    courses={state.courses}
                    errors={state.errors}
                    onChange={form => setState({ form })}
                    products={state.products}
                    value={state.form}
                />
            )}
        </div>
    );

};
